import { useState } from "react";
import api from "../../lib/api";
import Button from "../ui/Button";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DEFAULT_WINDOW = { start_time: "09:00", end_time: "18:00" };

// Weekly working hours, set per day — so a shop can open late on Saturday
// or close early on Friday.
export default function WorkingHours({ staffMember, onSaved }) {
  const initial = () => {
    const days = {};
    (staffMember.availability ?? [])
      .filter((w) => !w.is_blocked)
      .forEach((w) => {
        days[w.day_of_week] = { start_time: w.start_time?.slice(0, 5), end_time: w.end_time?.slice(0, 5) };
      });
    return days;
  };
  const [days, setDays] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (day) =>
    setDays((prev) => {
      const next = { ...prev };
      if (next[day]) delete next[day];
      else next[day] = { ...DEFAULT_WINDOW };
      return next;
    });

  const setTime = (day, field, value) => setDays((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));

  const save = async () => {
    const windows = Object.entries(days).map(([day, w]) => ({ day_of_week: Number(day), ...w }));
    if (windows.some((w) => !w.start_time || !w.end_time || w.start_time >= w.end_time)) {
      setError("Each closing time must be later than the opening time.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/owner/staff/${staffMember.id}/availability`, { windows });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save working hours.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 space-y-1.5">
      {DAYS.map((label, day) => (
        <div key={label} className="flex items-center gap-2 text-xs">
          <label className="flex w-24 items-center gap-1.5">
            <input type="checkbox" checked={Boolean(days[day])} onChange={() => toggle(day)} className="accent-primary" />
            {label.slice(0, 3)}
          </label>
          {days[day] ? (
            <>
              <input
                type="time"
                value={days[day].start_time}
                onChange={(e) => setTime(day, "start_time", e.target.value)}
                aria-label={`${label} opens`}
                className="h-8 rounded-md border border-input px-1"
              />
              <span>–</span>
              <input
                type="time"
                value={days[day].end_time}
                onChange={(e) => setTime(day, "end_time", e.target.value)}
                aria-label={`${label} closes`}
                className="h-8 rounded-md border border-input px-1"
              />
            </>
          ) : (
            <span className="text-muted-foreground">Day off</span>
          )}
        </div>
      ))}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <Button size="sm" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save hours"}
      </Button>
    </div>
  );
}
